import { ICommentDto } from "@kuika/kuika-cl-model";
import { AxiosResponse } from "axios";
import { CommentService } from "../../services/comment-service";
import type { ScreenDesignInstance } from "./designer-context";
import { getValues } from "./designer-context";

interface IFormattedComment {
  id: string;
  text: string;
  author: string;
  createdAt: Date;
  order: number;
  isEdited: boolean;
  kuikaId: string;
}

interface ICommentGroup {
  kuikaId: string;
  commentCount: number;
  comments: IFormattedComment[];
}

interface IListCommentsSuccess {
  count: number;
  groupCount?: number;
  groups?: ICommentGroup[];
  message?: string;
}

interface ICommentError {
  error: string;
}

interface ICreateCommentSuccess {
  success: true;
  comment: IFormattedComment;
}

interface IReplyCommentSuccess {
  success: true;
  comment: IFormattedComment;
  order: number;
}

function isSuccessfulResponse(res: AxiosResponse | undefined | null): boolean {
  return !!res && res.status >= 200 && res.status < 300;
}

function getCurrentScreenDesignId(sdInstance: ScreenDesignInstance): string | undefined {
  const sd = getValues(sdInstance);
  return sd?.getScreenDesign?.()?.id || sd?.sdId;
}

function getCommentAuthor(user: ICommentDto["createdUser"] | undefined): string {
  if (!user) return "Unknown";
  // The runtime IUser object may carry additional properties (e.g. email)
  // that are not declared on the IUser interface.
  const userRecord = user as ICommentDto["createdUser"] & Record<string, unknown>;
  return (
    user.username ||
    (typeof userRecord["userName"] === "string" ? userRecord["userName"] : undefined) ||
    (typeof userRecord["email"] === "string" ? userRecord["email"] : undefined) ||
    "Unknown"
  );
}

function formatComment(comment: ICommentDto): IFormattedComment {
  return {
    id: comment.id,
    text: comment.comment,
    author: getCommentAuthor(comment.createdUser),
    createdAt: comment.createdAt,
    order: comment.order,
    isEdited: comment.isEdited,
    kuikaId: comment.kuikaId?.toString()
  };
}

export async function listCommentsByCurrentScreen(
  sdInstance: ScreenDesignInstance
): Promise<ICommentError | IListCommentsSuccess> {
  const sdId = getCurrentScreenDesignId(sdInstance);
  if (!sdId) return { error: "No screen design loaded" };

  const res = await CommentService.GetAllByScreenDesignId(sdId);
  if (!isSuccessfulResponse(res)) {
    return { error: `Failed to fetch comments${res?.status ? `: ${res.status}` : ""}` };
  }

  const comments: ICommentDto[] = Array.isArray(res.data) ? res.data : [];
  if (comments.length === 0) {
    return { count: 0, message: "No comments on this screen", groups: [] };
  }

  const grouped: Record<string, IFormattedComment[]> = {};
  comments.forEach((comment: ICommentDto) => {
    const key = comment.kuikaId?.toString() || "unknown";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(formatComment(comment));
  });

  const groups = Object.entries(grouped).map(([kuikaId, thread]) => ({
    kuikaId,
    commentCount: thread.length,
    comments: thread.sort((left, right) => (left.order || 0) - (right.order || 0))
  }));

  return {
    count: comments.length,
    groupCount: groups.length,
    groups
  };
}

export async function createCommentOnCurrentScreen(
  sdInstance: ScreenDesignInstance,
  input: {
    kuikaId: string;
    text: string;
    order?: number;
  }
): Promise<ICommentError | ICreateCommentSuccess> {
  const sdId = getCurrentScreenDesignId(sdInstance);
  if (!sdId) return { error: "No screen design loaded" };

  const payload: ICommentDto = {
    comment: input.text,
    kuikaId: input.kuikaId.toString(),
    kScreenDesign_Id: sdId,
    order: input.order ?? 0
  } as ICommentDto;

  const res = await CommentService.Create(payload);
  if (!isSuccessfulResponse(res)) {
    return { error: `Failed to post comment${res?.status ? `: ${res.status}` : ""}` };
  }

  return {
    success: true,
    comment: formatComment(res.data)
  };
}

export async function replyToCommentThread(
  sdInstance: ScreenDesignInstance,
  input: {
    kuikaId: string;
    text: string;
  }
): Promise<ICommentError | IReplyCommentSuccess> {
  const commentsResult = await listCommentsByCurrentScreen(sdInstance);
  if ("error" in commentsResult) return commentsResult;

  const thread = commentsResult.groups?.find((group) => group.kuikaId === input.kuikaId.toString());
  if (!thread || thread.comments.length === 0) {
    return {
      error: `No existing comments found on component ${input.kuikaId}. Use ui_project_post_comment to start a new thread.`
    };
  }

  const order = Math.max(...thread.comments.map((comment) => comment.order || 0)) + 1;
  const createResult = await createCommentOnCurrentScreen(sdInstance, {
    kuikaId: input.kuikaId,
    text: input.text,
    order
  });

  if ("error" in createResult) return createResult;

  return {
    success: true,
    order,
    comment: createResult.comment
  };
}
